import React, { useMemo } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Terminal, Users, Settings, Bell, Zap, Database, Wrench,
} from "lucide-react";
import { useAlertWatcher } from "@/lib/useAlertWatcher";
import { Sidebar } from "@/components/shell/Sidebar";
import { TopBar } from "@/components/shell/TopBar";
import { NoProjectAccess } from "@/components/shell/NoProjectAccess";

export { NoProjectAccess };

const NAV_ITEMS = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testId: "nav-overview" },
  { to: "/faults", label: "Faults", icon: Wrench, testId: "nav-faults" },
  { to: "/sites", label: "Sites", icon: Building2, testId: "nav-sites" },
  { to: "/energy", label: "Energy", icon: Zap, testId: "nav-energy" },
  { to: "/alerts", label: "Alerts", icon: Bell, testId: "nav-alerts" },
  { to: "/projects", label: "Data Sources", icon: Database, testId: "nav-projects", adminOnly: true },
  { to: "/axon", label: "Query Console", icon: Terminal, testId: "nav-axon", adminOnly: true },
  { to: "/users", label: "Users", icon: Users, testId: "nav-users", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const activeAlerts = useAlertWatcher();

  const nav = useMemo(
    () =>
      NAV_ITEMS
        .filter((n) => !n.adminOnly || user?.role === "admin")
        .map((n) => (n.to === "/alerts" ? { ...n, badge: activeAlerts } : n)),
    [activeAlerts, user?.role]
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F3F8F7] text-[#212121]">
      <div className="flex min-h-screen items-start">
        <Sidebar nav={nav} />
        <main className="flex-1 flex flex-col min-w-0">
          <TopBar user={user} onLogout={handleLogout} />
          <section className="relative flex-1 px-6 md:px-10 py-8">
            <Outlet />
          </section>
        </main>
      </div>
    </div>
  );
}
